import React from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment";

import PageTitle from "../../../layouts/PageTitle";
import {
  useGetWorkProgressReportQuery,
  useGetDailyRosterQuery,
} from "../../../../store/appApi";

const SHIFT_LABEL = { morning: "Morning ☀", evening: "Evening 🌙" };

const ATTENDANCE_LABEL = {
  PRESENT:        "Present",
  PARTIAL:        "Partial",
  ON_LEAVE:       "On Leave",
  HALF_DAY_LEAVE: "Half Day",
  PENDING:        "Leave Pending",
  ABSENT:         "Absent",
  HOLIDAY:        "Holiday",
  UPCOMING:       "Upcoming",
};

const ATTENDANCE_COLOR = {
  PRESENT:        "#2bc155",
  PARTIAL:        "#f6a821",
  ON_LEAVE:       "#3f9ae0",
  HALF_DAY_LEAVE: "#3f9ae0",
  PENDING:        "#ff9f43",
  ABSENT:         "#f32d2d",
  HOLIDAY:        "#8b5cf6",
  UPCOMING:       "#64748b",
};

// ── Tiny read-only section wrapper ────────────────────────────────────────────
const Section = ({ number, title, children }) => (
  <div className="card mb-4">
    <div className="card-header d-flex align-items-center gap-2 py-2 bg-primary bg-opacity-10">
      <h5 className="mb-0 text-white" style={{ fontSize: 14, fontWeight: 600 }}>
        <span className="badge bg-primary me-2" style={{ fontSize: 13 }}>
          {number}
        </span>
        {number} — {title}
      </h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Empty = () => <p className="text-muted mb-0">— No entries recorded —</p>;

const WorkProgressDetail = () => {
  const { id } = useParams();
  const { data, isLoading, isError } = useGetWorkProgressReportQuery(id);
  const r = data?.report;

  const rosterDate = r?.reportDateTime
    ? new Date(r.reportDateTime).toISOString().split("T")[0]
    : null;
  const { data: rosterData } = useGetDailyRosterQuery(
    { date: rosterDate, projectId: r?.projectId?._id },
    { skip: !rosterDate },
  );
  const attendanceByEmployeeId = (rosterData?.roster ?? []).reduce(
    (map, row) => ({ ...map, [row.employeeId]: row.tag }),
    {},
  );

  if (isLoading) {
    return (
      <>
        <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </>
    );
  }

  if (isError || !r) {
    return (
      <>
        <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />
        <div className="alert alert-danger">
          Could not load report.{" "}
          <Link to="/progress/workprogress/list">Back to list</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />

      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">
            {r.projectId?.projectName ?? "—"}
            <span className="ms-3 badge badge-primary light fs-6">
              {SHIFT_LABEL[r.reportType] ?? r.reportType}
            </span>
          </h4>
          <small className="text-muted">
            {moment(r.reportDateTime).format("dddd, DD MMMM YYYY — hh:mm A")}
          </small>
        </div>
        <div className="d-flex gap-2">
          <Link
            to={`/progress/workprogress/edit/${r._id}`}
            className="btn-sm"
            style={{
              backgroundColor: "#fff",
              color: "#324336",
              border: "1px solid #324336",
            }}
          >
            <i className="la la-pencil me-1" />
            Edit Report
          </Link>
          <Link
            to="/progress/workprogress/list"
            className="btn btn-light btn-sm"
          >
            ← Back to List
          </Link>
        </div>
      </div>

      {/* ── Section 1: Core Meta ─────────────────────────────────────────── */}
      <Section number={1} title="Core Report Details">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label text-muted small mb-1">Project</label>
            <p className="fw-semibold mb-0">
              {r.projectId?.projectName ?? "—"}
              {r.projectId?.city && (
                <small className="text-muted ms-1">({r.projectId.city})</small>
              )}
            </p>
          </div>
          <div className="col-md-4">
            <label className="form-label text-muted small mb-1">Shift</label>
            <p className="fw-semibold mb-0">
              {SHIFT_LABEL[r.reportType] ?? r.reportType}
            </p>
          </div>
          <div className="col-md-4">
            <label className="form-label text-muted small mb-1">
              Date &amp; Time
            </label>
            <p className="fw-semibold mb-0">
              {moment(r.reportDateTime).format("DD MMM YYYY — hh:mm A")}
            </p>
          </div>
          <div className="col-md-4">
            <label className="form-label text-muted small mb-1">
              Reported By
            </label>
            <p className="fw-semibold mb-0">
              {r.reportedBy?.name ?? "—"}
              {r.reportedBy?.role ? `-${r.reportedBy.role}` : ""}
              {attendanceByEmployeeId[r.reportedBy?._id] && (
                <small
                  className="ms-1"
                  style={{
                    color:
                      ATTENDANCE_COLOR[attendanceByEmployeeId[r.reportedBy._id]] ||
                      "inherit",
                  }}
                >
                  ({ATTENDANCE_LABEL[attendanceByEmployeeId[r.reportedBy._id]] ||
                    attendanceByEmployeeId[r.reportedBy._id]})
                </small>
              )}
            </p>
          </div>
          <div className="col-md-4">
            <label className="form-label text-muted small mb-1">
              Logged On
            </label>
            <p className="fw-semibold mb-0">
              {moment(r.created).format("DD MMM YYYY, hh:mm A")}
            </p>
          </div>
        </div>
      </Section>

      {/* ── Section 2A: Equipment ─────────────────────────────────────────── */}
      <Section number="2A" title="Equipment Deployment">
        {r.equipments?.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Equipment</th>
                  <th>Type</th>
                  <th>Number</th>
                  <th>Qty Deployed</th>
                </tr>
              </thead>
              <tbody>
                {r.equipments.map((e, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{e.equipmentId?.eqpName ?? "—"}</td>
                    <td>{e.equipmentId?.eqpType ?? "—"}</td>
                    <td>{e.equipmentId?.eqpNumber ?? "—"}</td>
                    <td>{e.eqpQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </Section>

      {/* ── Section 2B: Work Execution ────────────────────────────────────── */}
      <Section number="2B" title="Work Execution &amp; Manpower">
        {r.workExecution?.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Type of Work</th>
                  <th>Labour Engaged</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Group rows sharing the same typeOfWork so it's shown once,
                  // with all its labour lines listed together.
                  const groups = [];
                  r.workExecution.forEach((w) => {
                    let group = groups.find((g) => g.typeOfWork === w.typeOfWork);
                    if (!group) {
                      group = { typeOfWork: w.typeOfWork, labour: [] };
                      groups.push(group);
                    }
                    if (w.labourEngaged) {
                      group.labour.push({ type: w.labourEngaged, count: w.numberOfLabour });
                    }
                  });
                  return groups.map((g, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{g.typeOfWork || "—"}</td>
                      <td>
                        {g.labour.length ? (
                          <div className="d-flex flex-wrap gap-1">
                            {g.labour.map((l, li) => (
                              <span key={li} className="badge badge-primary light">
                                {l.type}-{l.count}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </Section>

      {/* ── Section 3: Contractor Engagement ─────────────────────────────── */}
      <Section number={3} title="Contractor Engagement">
        {r.contractorDetails?.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Contractor</th>
                  <th>Phone</th>
                  <th>Skilled</th>
                  <th>Unskilled</th>
                  <th>Work Assigned</th>
                </tr>
              </thead>
              <tbody>
                {r.contractorDetails.map((c, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="fw-semibold">
                      {c.contractorId?.name ?? "—"}
                    </td>
                    <td>{c.contractorId?.phone ?? "—"}</td>
                    <td>{c.skilledLabour}</td>
                    <td>{c.unskilledLabour}</td>
                    <td>{c.workAssigned || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </Section>

      {/* ── Section 4: Internal Staff ─────────────────────────────────────── */}
      <Section number={4} title="Internal Staff Deployment">
        {r.employeeDetails?.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Assigned Location</th>
                </tr>
              </thead>
              <tbody>
                {r.employeeDetails.map((emp, i) => {
                  const status = attendanceByEmployeeId[emp.employeeId?._id];
                  return (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="fw-semibold">
                        {emp.employeeId?.name ?? "—"}
                        {emp.employeeId?.role ? `-${emp.employeeId.role}` : ""}
                        {status && (
                          <span
                            className="ms-1"
                            style={{ color: ATTENDANCE_COLOR[status] || "inherit" }}
                          >
                            ({ATTENDANCE_LABEL[status] || status})
                          </span>
                        )}
                      </td>
                      <td>{emp.locationName || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </Section>

      {/* ── Section 5: Material Issuance ──────────────────────────────────── */}
      <Section number={5} title="Material Issuance &amp; Stock Allocation">
        {r.materialDetails?.length ? (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Material</th>
                  <th>Unit</th>
                  <th>Qty Issued</th>
                  <th>Issued To (Contractor)</th>
                  <th>Balance at Time of Issue</th>
                </tr>
              </thead>
              <tbody>
                {r.materialDetails.map((m, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="fw-semibold">
                      {m.materialId?.skuName ?? "—"}
                    </td>
                    <td>{m.materialId?.measurement ?? "—"}</td>
                    <td>
                      <span className="badge badge-danger light">
                        {m.qtyIssued}
                      </span>
                    </td>
                    <td>{m.issuedToContractorId?.name ?? "—"}</td>
                    <td className="text-muted">
                      {m.currentStockBalanceSnapshot ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty />
        )}
      </Section>

      {/* ── Bottom navigation ──────────────────────────────────────────────── */}
      <div className="d-flex gap-2 mb-5">
        <Link
          to={`/progress/workprogress/edit/${r._id}`}
          className="btn btn-warning"
        >
          Edit This Report
        </Link>
        <Link to="/progress/workprogress/list" className="btn btn-light">
          ← Back to List
        </Link>
      </div>
    </>
  );
};

export default WorkProgressDetail;
